
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { ShoppingItem } from '@/types/shoppingList';
import { formatSmartQuantity } from '@/utils/quantityFormatter';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

interface MobileShoppingItemProps {
  item: ShoppingItem;
  onCheckedChange: (checked: boolean) => void;
  isCustomItem?: boolean;
  onRemove?: () => void;
}

const MobileShoppingItem: React.FC<MobileShoppingItemProps> = ({
  item,
  onCheckedChange,
  isCustomItem = false,
  onRemove,
}) => {
  // Helper to format item quantity based on its properties
  const formatItemQuantity = (item: ShoppingItem): string => {
    if (item.staple) return ""; // No quantity for staple items
    if (item.forServing) return "to taste"; // Special format for serving items
    
    // Apply smart formatting to regular quantities
    return formatSmartQuantity(item.quantity);
  };

  const quantity = formatItemQuantity(item);

  return (
    <div 
      className={cn(
        "flex items-center gap-4 p-4 bg-white border rounded-lg shadow-sm transition-all duration-200 min-h-[60px]",
        isCustomItem 
          ? "border-l-4 border-l-violet-400 border-t-gray-200 border-r-gray-200 border-b-gray-200 bg-violet-50/30" 
          : "border-gray-200",
        item.checked ? "bg-gray-50 opacity-75" : "hover:shadow-md active:scale-[0.98]"
      )}
    >
      <Checkbox 
        id={`item-${item.id}`}
        checked={item.checked}
        onCheckedChange={onCheckedChange}
        className="h-6 w-6 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className={cn(
          "text-lg font-medium leading-tight",
          item.checked ? "line-through text-gray-500" : "text-gray-900"
        )}>
          {quantity && (
            <span className="text-primary mr-2 font-semibold">{quantity}</span>
          )}
          {item.name}
        </div>
        {item.note && (
          <div className="text-sm text-gray-500 mt-1">{item.note}</div>
        )}
      </div>
      {isCustomItem && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          aria-label="Remove item"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default MobileShoppingItem;
